import React, { useEffect, useState } from 'react'
import { motion, useSpring, useTransform, animate } from 'framer-motion'
import { FiUsers, FiZap, FiTrash2, FiDroplet, FiClock, FiHeart, FiActivity } from 'react-icons/fi'
import { fetchPublicImpactStats } from '../utils/api'

const Counter = ({ value, suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const controls = animate(0, value, {
      duration: 2,
      onUpdate: (latest) => setDisplayValue(Math.floor(latest)),
      ease: "easeOut"
    })
    return () => controls.stop()
  }, [value])

  return (
    <span className="tabular-nums">
      {displayValue.toLocaleString()}{suffix}
    </span>
  )
}

const ImpactDashboard = () => {
  const [dbStats, setDbStats] = useState(null)

  useEffect(() => {
    const getStats = async () => {
      try {
        const res = await fetchPublicImpactStats()
        setDbStats(res.data?.data)
      } catch (err) {
        console.error('Failed to fetch public impact stats:', err)
      }
    }
    getStats()
  }, [])

  const defaultStats = {
    total_volunteers: 5000,
    active_missions: 48,
    waste_removed_kg: 125000,
    lakes_cleaned: 8,
    hours_contributed: 45000,
    community_members: 12500
  }

  const currentStats = dbStats || defaultStats

  const stats = [
    {
      icon: FiUsers,
      label: 'TOTAL VOLUNTEERS',
      value: Number(currentStats.total_volunteers) || 0,
      suffix: "+",
      color: '#800000',
      trend: "+12% this month"
    },
    {
      icon: FiZap,
      label: 'ACTIVE MISSIONS',
      value: Number(currentStats.active_missions) || 0,
      suffix: "",
      color: '#800000',
      trend: "6 live now"
    },
    {
      icon: FiTrash2,
      label: 'WASTE REMOVED',
      value: Number(currentStats.waste_removed_kg) || 0,
      suffix: " KG",
      color: '#800000',
      trend: "Eco-impact high"
    },
    {
      icon: FiDroplet,
      label: 'LAKES CLEANED',
      value: Number(currentStats.lakes_cleaned) || 0,
      suffix: "",
      color: '#800000',
      trend: "Blue zone active"
    },
    {
      icon: FiClock,
      label: 'HOURS GIVEN',
      value: Number(currentStats.hours_contributed) || 0,
      suffix: "H",
      color: '#800000',
      trend: "Community power"
    },
    {
      icon: FiHeart,
      label: 'IMPACT SCORE',
      value: 98,
      suffix: "%",
      color: '#800000',
      trend: "Top 1% City"
    },
  ]

  return (
    <section id="impact" className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-[0.02] pointer-events-none select-none">
        <h2 className="text-[20vw] font-black font-grotesk tracking-tighter leading-none text-gray-300">IMPACT</h2>
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div className="text-center mb-32">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="inline-flex items-center space-x-2 bg-white/10 border border-gray-200 px-4 py-2 rounded-full mb-8">
            <FiActivity className="text-accent-maroon animate-pulse" />
            <span className="text-accent-maroon font-black text-[10px] uppercase tracking-widest">Live Network Activity</span>
          </motion.div>
          
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-6xl md:text-8xl font-black font-grotesk tracking-tighter leading-none text-gray-800 mb-6">
            THE CITY <br />
            <span className="bg-gradient-to-r from-accent-maroon via-accent-maroonDark to-accent-maroon bg-clip-text text-transparent">PULSE.</span>
          </motion.h2>
          <p className="text-gray-600 text-xl font-medium max-w-2xl mx-auto leading-relaxed">
            Every action is tracked. Every volunteer counts. Witness the real-time transformation of Udaipur's landscape.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative bg-white/10 border border-gray-200 rounded-[2.5rem] p-10 backdrop-blur-xl overflow-hidden transition-all duration-500 hover:border-accent-maroon/30"
              >
                <div className="absolute -top-20 -right-20 w-40 h-40 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-full blur-[60px]" style={{ backgroundColor: stat.color }} />

                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-8 border transition-all duration-500 group-hover:scale-110" style={{ backgroundColor: `${stat.color}10`, borderColor: `${stat.color}30`, color: stat.color }}>
                    <Icon size={28} />
                  </div>

                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] block mb-2">{stat.label}</span>

                  <div className="text-5xl font-black text-gray-800 tracking-tighter mb-4 flex items-baseline">
                    <Counter value={stat.value} suffix={stat.suffix} />
                  </div>

                  <div className="flex items-center space-x-2 pt-6 border-t border-gray-200 mt-auto">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-maroon animate-pulse" />
                    <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">{stat.trend}</span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ImpactDashboard